// ─── THEMES (aligned with communities.primary_sub_theme_family) ───
export type TribeName =
  | "Sports"
  | "Wellness & Health"
  | "Arts & Culture"
  | "Entertainment & Media"
  | "Technology"
  | "Business"
  | "Family & Life Stages"
  | "Society & Causes"
  | "Crafts & Making"
  | "Passions & Collecting";

export const TRIBES: TribeName[] = [
  "Sports",
  "Wellness & Health",
  "Arts & Culture",
  "Entertainment & Media",
  "Technology",
  "Business",
  "Family & Life Stages",
  "Society & Causes",
  "Crafts & Making",
  "Passions & Collecting",
];

export const TRIBE_EMOJIS: Record<TribeName, string> = {
  "Sports": "🏃",
  "Wellness & Health": "🧘",
  "Arts & Culture": "🎨",
  "Entertainment & Media": "🎬",
  "Technology": "💻",
  "Business": "💼",
  "Family & Life Stages": "👨‍👩‍👧",
  "Society & Causes": "🤝",
  "Crafts & Making": "🛠️",
  "Passions & Collecting": "🎯",
};

// ─── SUB-THEMES (legacy static list — sub-themes are now derived dynamically) ───
export const SUB_THEMES = [
  "Running",
  "Tennis & Racket",
  "Entrepreneurship & Business",
  "Startups & Entrepreneurship",
  "Product & UX",
] as const;
export type SubTheme = (typeof SUB_THEMES)[number];

// ─── COMMUNITY VALUES (canonical taxonomy) ───
export const COMMUNITY_VALUES = [
  "Performance",
  "Authenticity",
  "Empowerment",
  "Inclusivity",
  "Impact",
  "Exclusivity",
  "Belonging",
  "Adventure",
  "Creativity",
  "Fun & Playfulness",
  "Sustainability",
  "Excellence",
  "Ambition",
  "Innovation",
  "Tradition",
  "Prestige",
  "Solidarity",
] as const;
export type CommunityValue = (typeof COMMUNITY_VALUES)[number];

// ─── MEMBERS INTENTION (canonical taxonomy) ───
export const MEMBER_DRIVERS = [
  "Connect & Belong",
  "Network Professionally",
  "Learn & Master",
  "Push My Limits",
  "Express Myself",
  "Make an Impact",
  "Find Balance",
  "Celebrate & Share",
] as const;
export type MemberDriver = (typeof MEMBER_DRIVERS)[number];

// ─── ACTIVATION TYPES ───
export const ACTIVATION_TYPES = [
  "Ambassador Programme",
  "Branded Content",
  "Co-created Event",
  "Event Sponsorship",
  "Product Seeding",
  "Product Testing",
  "Retreat / Residency",
] as const;
export type ActivationType = (typeof ACTIVATION_TYPES)[number];

// ─── GOVERNANCE ───
export const GOVERNANCE_MODELS = ["brand", "collective", "institution"] as const;
export type GovernanceModel = (typeof GOVERNANCE_MODELS)[number];

export const GOVERNANCE_ACTORS_BY_MODEL: Record<GovernanceModel, string[]> = {
  brand: ["brand community", "employee club"],
  collective: ["community"],
  institution: [
    "student association",
    "sports association",
    "NGO",
    "professional association",
  ],
};

// ─── GENDER PROFILE ───
export const GENDER_PROFILES = [
  "Predominantly men",
  "Skewing men",
  "Equal mix",
  "Skewing women",
  "Predominantly women",
] as const;
export type GenderProfile = (typeof GENDER_PROFILES)[number];

// ─── AGE BUCKETS (community_age_ranges.age_range values) ───
export const AGE_BUCKETS = ["Under 18", "18–24", "25–34", "35–44", "45–54", "55–64", "65+"] as const;
export type AgeBucket = (typeof AGE_BUCKETS)[number];

// ─── BREAKDOWN INTERFACES ───
export interface TribeBreakdown {
  tribe: string;
  percentage: number;
  microTribes: { name: string; percentage: number }[];
}

// Kept for component compatibility but no longer used in filters
export const MICRO_TRIBES: Record<TribeName, string[]> = {
  "Sports": ["Running", "Tennis & Racket"],
  "Wellness & Health": [],
  "Arts & Culture": [],
  "Entertainment & Media": [],
  "Technology": ["Startups & Entrepreneurship", "Product & UX"],
  "Business": ["Entrepreneurship & Business"],
  "Family & Life Stages": [],
  "Society & Causes": [],
  "Crafts & Making": [],
  "Passions & Collecting": [],
};

// ─── INTENTION / DRIVER (kept for Community DNA charts) ───
export const INTENTION_DRIVERS = [
  "Performance & Improvement",
  "Balance & Well-being",
  "Identity & Belonging",
  "Exploration & Novelty",
  "Impact & Ethics",
] as const;
export type IntentionDriver = (typeof INTENTION_DRIVERS)[number];

// ─── CONSUMPTION PATTERN (kept for Community DNA charts) ───
export const CONSUMPTION_PATTERNS = [
  "Habit-Driven",
  "Social",
  "Experimental",
  "Premium",
  "Pragmatic",
  "Aspirational",
  "Functional",
] as const;
export type ConsumptionPattern = (typeof CONSUMPTION_PATTERNS)[number];

export interface DriverBreakdown {
  name: string;
  percentage: number;
}

export interface PatternBreakdown {
  name: string;
  percentage: number;
}
